"use client";

import { useState, useEffect, Fragment } from "react";
import ProgressBar from "../components/common/progressBar";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Upload,
  Settings,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
  ChevronsUpDown,
  ChevronUp,
} from "lucide-react";
import { Snackbar, Alert } from "@mui/material";
import {
  getLessonProgress,
  getLessonUsers,
  resetTrainingProgress,
  createLesson,
  getAllRestaurants,
  getMenus,
  bulkUploadLessons,
} from "../services/lessonProgress";
import Dropdown from "../components/common/dropdown";
import authService from "../services/authService";
import { v4 as uuidv4 } from "uuid";
import UserDetails from "./UserDetails";

export default function LessonProgress() {
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showResetOptions, setShowResetOptions] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRestaurant, setSelectedRestaurant] =
    useState("All Restaurants");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState({
    activeEmployees: 0,
    inActiveEmployees: 0,
    overDueEmployees: 0,
    foodKnowledge: 0,
    foodUnits: {},
    wineKnowledge: 0,
    wineUnits: {},
    beerKnowledge: 0,
    beerUnits: {},
    recentActivities: [],
  });
  const [users, setUsers] = useState([]);
  const [showLessonDrawer, setShowLessonDrawer] = useState(false);
  const [lessonForm, setLessonForm] = useState({
    category: "food",
    unit: 1,
    unit_name: "",
    chapter: 1,
    chapter_name: "",
    questions: [],
    glossary: "",
    difficulty: "beginner",
    content: { section1: "", section2: "" },
    restaurant_uuid: "92cd5a0a-cf6e-4639-a2eb-ed831427dccd",
    menu_items: [],
  });
  const [questionDraft, setQuestionDraft] = useState({
    question_text: "",
    hint: "",
    question_type: "multiple_choice",
    options_variable: [""],
    correct_answer_variable: [""],
    difficulty: "easy",
    repeat_for: { key_variable: "", source: "" },
  });
  const [lessonLoading, setLessonLoading] = useState(false);
  const [lessonError, setLessonError] = useState("");
  const [lessonSuccess, setLessonSuccess] = useState("");
  const [restaurants, setRestaurants] = useState([]);
  const [menus, setMenus] = useState([]);
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6); // Renaming this in usage to activityItemsPerPage would be cleaner but I will just leave it and create new ones for the table to avoid breaking the activity section which I don't need to touch deeply.
  const [activityItemsPerPage, setActivityItemsPerPage] = useState(6);
  const [tableItemsPerPage, setTableItemsPerPage] = useState(10);
  const [tableCurrentPage, setTableCurrentPage] = useState(1);
  const [showAllFoodUnits, setShowAllFoodUnits] = useState(false);
  const [showAllWineUnits, setShowAllWineUnits] = useState(false);
  const [questionDraftError, setQuestionDraftError] = useState({});
  const [lessonFormErrors, setLessonFormErrors] = useState({});
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [resetVariable, setResetVariable] = useState("");
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [sortBy, setSortBy] = useState("status"); // Default sort by status (Overdue first)
  const [sortDirection, setSortDirection] = useState("asc"); // asc for Overdue first

  const userRole = localStorage.getItem("userRole");
  const [activeFrequencyMenu, setActiveFrequencyMenu] = useState(null);

  const canEditFrequency = ["manager", "director", "super_admin", "admin"].includes(
    userRole
  );

  const fetchLessonProgress = async () => {
    try {
      const [progressData, usersData, restaurantsData] = await Promise.all([
        getLessonProgress(),
        getLessonUsers(),
        getAllRestaurants(),
      ]);
      setProgressData(progressData);
      setUsers(usersData.usersWithDetails);
      console.log(usersData.usersWithDetails);
      console.log(progressData.lesson_frequency);
      setRestaurants(restaurantsData.restaurants || restaurantsData);
      setMenus(restaurantsData.menus || restaurantsData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLessonProgress();
  }, []);

  useEffect(() => {
    if (showLessonDrawer) {
      getAllRestaurants()
        .then((data) => setRestaurants(data.restaurants || data))
        .catch(() => setRestaurants([]));
      getMenus()
        .then((data) => setMenus(data.menus || data))
        .catch(() => setMenus([]));
    }
  }, [showLessonDrawer]);

  const handleSelectEmployee = (employeeId) => {
    if (selectedEmployees.includes(employeeId)) {
      setSelectedEmployees(selectedEmployees.filter((id) => id !== employeeId));
    } else {
      setSelectedEmployees([...selectedEmployees, employeeId]);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedEmployees(filteredUsers.map((user) => user.uuid));
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleResetTraining = async () => {
    try {
      setShowResetConfirmation(true);
      const resetUsers =
        selectedEmployees.length === users.length ? "all" : selectedEmployees;
      await resetTrainingProgress(resetUsers, resetVariable);

      // Refresh the data after reset
      const [progressData, usersData] = await Promise.all([
        getLessonProgress(),
        getLessonUsers(),
      ]);
      setProgressData(progressData);
      setUsers(usersData.usersWithDetails);
      setShowResetConfirmation(false);
      setResetVariable("");
      setShowResetOptions(false);
      setSelectedEmployees([]);
      setSnackbarMessage("Training reset successfully.");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error in handleResetTraining:", error);
      setShowResetConfirmation(false);
      setSnackbarMessage("Failed to reset training.");
      setSnackbarOpen(true);
    }
  };

  const toggleHistory = (employeeId) => {
    if (expandedHistory === employeeId) {
      setExpandedHistory(null);
    } else {
      setExpandedHistory(employeeId);
    }
  };

  // Debounce search query
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = () => setActiveFrequencyMenu(null);
    if (activeFrequencyMenu) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [activeFrequencyMenu]);

  const [selectedFrequency, setSelectedFrequency] = useState("All");

  const handleClearFilters = () => {
    setSearchQuery(""); // This will also trigger debounce update
    setSelectedStatus("All");
    setSelectedFrequency("All");
    setSelectedRestaurant("All Restaurants");
    setTableCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction: asc -> desc -> none
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection(null);
        setSortBy(null);
      } else {
        setSortDirection("asc");
      }
    } else {
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  const sortData = (data) => {
    if (!sortBy || !sortDirection) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "name":
          aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
          bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;

        case "restaurant":
          // Use the first restaurant name for sorting, or empty string if none
          aValue = (a.restaurants?.[0]?.name || a.restaurants?.[0] || "").toString().toLowerCase();
          bValue = (b.restaurants?.[0]?.name || b.restaurants?.[0] || "").toString().toLowerCase();
          break;

        case "food":
          // Sort by underlying numeric percentage value
          aValue = a.foodKnowledge || 0;
          bValue = b.foodKnowledge || 0;
          break;

        case "wine":
          // Sort by underlying numeric percentage value
          aValue = a.wineKnowledge || 0;
          bValue = b.wineKnowledge || 0;
          break;

        case "beer":
          aValue = a.beerKnowledge || 0;
          bValue = b.beerKnowledge || 0;
          break;

        case "frequency":
          // Sort by numeric day value (extract number from "3 day", "5 day", etc.)
          aValue =
            parseInt((a.frequency || "0").toString().replace(/\D/g, "")) || 0;
          bValue =
            parseInt((b.frequency || "0").toString().replace(/\D/g, "")) || 0;
          break;

        case "status":
          // Custom priority order: Overdue -> On Track -> others
          const statusOrder = { Overdue: 1, "On Track": 2 };
          aValue = statusOrder[a.status] || 3;
          bValue = statusOrder[b.status] || 3;
          // If same priority, sort by name as secondary sort for stability
          if (aValue === bValue) {
            aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
            bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
          }
          break;

        default:
          return 0;
      }

      if (sortDirection === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const filteredUsers = users.filter((user) => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    // Search by name or email
    const email = (user.email || "").toLowerCase();
    const query = debouncedSearchQuery.trim().toLowerCase();

    // Search by name or email
    const matchesSearch = fullName.includes(query) || email.includes(query);

    const matchesRestaurant =
      selectedRestaurant === "All Restaurants" ||
      (user.restaurants && Array.isArray(user.restaurants) &&
        user.restaurants.some((r) => {
          const rName = (r?.name || r || "").toString().trim().toLowerCase();
          const sName = selectedRestaurant.trim().toLowerCase();
          return rName === sName;
        }));

    const matchesStatus =
      selectedStatus === "All" ||
      (selectedStatus === "On Track"
        ? user.status === "On Track"
        : user.status === "Overdue");

    const matchesFrequency =
      selectedFrequency === "All" ||
      parseInt(user.frequency || 3) === parseInt(selectedFrequency);

    return (
      matchesSearch && matchesRestaurant && matchesStatus && matchesFrequency
    );
  });

  // Apply sorting after filtering
  const sortedUsers = sortData(filteredUsers);

  const totalTableItems = sortedUsers.length;
  const totalTablePages = Math.ceil(totalTableItems / tableItemsPerPage);
  const paginatedUsers = sortedUsers.slice(
    (tableCurrentPage - 1) * tableItemsPerPage,
    tableCurrentPage * tableItemsPerPage
  );

  // Reset to page 1 if filtered results change and current page is out of bounds
  useEffect(() => {
    if (tableCurrentPage > totalTablePages && totalTablePages > 0) {
      setTableCurrentPage(1);
    }
  }, [filteredUsers.length, totalTablePages]);

  // Reset page when filters change (using useEffects for each filter would be cleaner, or just wrapping the setters)
  useEffect(() => {
    setTableCurrentPage(1);
  }, [
    debouncedSearchQuery,
    selectedRestaurant,
    selectedStatus,
    selectedFrequency,
  ]);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleLessonFormChange = (e) => {
    const { name, value } = e.target;
    setLessonForm((prev) => ({ ...prev, [name]: value }));
    // Clear error for the field being changed
    setLessonFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };
  const handleLessonContentChange = (e) => {
    const { name, value } = e.target;
    setLessonForm((prev) => ({
      ...prev,
      content: { ...prev.content, [name]: value },
    }));
  };
  const validateQuestionDraft = (q) => {
    const err = {};
    if (!q.question_text) err.question_text = "Question text is required.";
    if (!q.question_type) err.question_type = "Type is required.";
    if (!q.difficulty) err.difficulty = "Difficulty is required.";
    if (
      q.question_type === "single_select" ||
      q.question_type === "multiple_choice"
    ) {
      if (
        !q.options_variable ||
        q.options_variable.length < 2 ||
        q.options_variable.some((opt) => !opt)
      ) {
        err.options_variable =
          "At least two options are required, none can be empty.";
      }
      if (
        !q.correct_answer_variable ||
        q.correct_answer_variable.length < 1 ||
        q.correct_answer_variable.some((ans) => !ans)
      ) {
        err.correct_answer_variable =
          "At least one correct answer is required, none can be empty.";
      }
    } else {
      if (
        !q.correct_answer_variable ||
        q.correct_answer_variable.some((ans) => !ans)
      ) {
        err.correct_answer_variable = "Correct answer is required.";
      }
    }
    return err;
  };

  const handleAddQuestion = () => {
    const err = validateQuestionDraft(questionDraft);
    setQuestionDraftError(err);
    if (Object.keys(err).length > 0) return;
    setLessonForm((prev) => ({
      ...prev,
      questions: [...prev.questions, { ...questionDraft, uuid: uuidv4() }],
    }));
    setQuestionDraft({
      question_text: "",
      hint: "",
      question_type: "multiple_choice",
      options_variable: [""],
      correct_answer_variable: [""],
      difficulty: "easy",
      repeat_for: { key_variable: "", source: "" },
    });
    setQuestionDraftError({});
  };
  const handleQuestionDraftChange = (e) => {
    const { name, value } = e.target;
    setQuestionDraft((prev) => ({ ...prev, [name]: value }));
  };
  const handleQuestionOptionsChange = (idx, value) => {
    setQuestionDraft((prev) => {
      const options = [...prev.options_variable];
      options[idx] = value;
      return { ...prev, options_variable: options };
    });
  };
  const handleCorrectAnswerChange = (idx, value) => {
    setQuestionDraft((prev) => {
      const answers = [...prev.correct_answer_variable];
      answers[idx] = value;
      return { ...prev, correct_answer_variable: answers };
    });
  };
  const handleAddOption = () => {
    setQuestionDraft((prev) => ({
      ...prev,
      options_variable: [...prev.options_variable, ""],
    }));
  };
  const handleAddCorrectAnswer = () => {
    setQuestionDraft((prev) => ({
      ...prev,
      correct_answer_variable: [...prev.correct_answer_variable, ""],
    }));
  };
  const handleRemoveQuestion = (idx) => {
    setLessonForm((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== idx),
    }));
  };
  const validateLessonForm = (form) => {
    const errors = {};
    if (!form.category) errors.category = "Category is required.";
    if (!form.unit) errors.unit = "Unit is required.";
    if (!form.unit_name) errors.unit_name = "Unit name is required.";
    if (!form.chapter) errors.chapter = "Chapter is required.";
    if (!form.chapter_name) errors.chapter_name = "Chapter name is required.";
    if (!form.restaurant_uuid)
      errors.restaurant_uuid = "Restaurant is required.";
    if (!form.menu_items || form.menu_items.length === 0)
      errors.menu_items = "At least one menu item is required.";
    if (!form.difficulty) errors.difficulty = "Difficulty is required.";
    if (!form.content || !form.content.section1)
      errors.content = { section1: "Content Section 1 is required." };
    if (!form.questions || form.questions.length < 1) {
      errors.questions = "At least one question is required.";
    } else {
      errors.questions = [];
      form.questions.forEach((q, idx) => {
        const qErr = {};
        if (!q.question_text) qErr.question_text = "Question text is required.";
        if (!q.question_type) qErr.question_type = "Type is required.";
        if (!q.difficulty) qErr.difficulty = "Difficulty is required.";
        if (
          q.question_type === "single_select" ||
          q.question_type === "multiple_choice"
        ) {
          if (
            !q.options_variable ||
            q.options_variable.length < 2 ||
            q.options_variable.some((opt) => !opt)
          ) {
            qErr.options_variable =
              "At least two options are required, none can be empty.";
          }
          if (
            !q.correct_answer_variable ||
            q.correct_answer_variable.length < 1 ||
            q.correct_answer_variable.some((ans) => !ans)
          ) {
            qErr.correct_answer_variable =
              "At least one correct answer is required, none can be empty.";
          }
        } else {
          if (
            !q.correct_answer_variable ||
            q.correct_answer_variable.some((ans) => !ans)
          ) {
            qErr.correct_answer_variable = "Correct answer is required.";
          }
        }
        errors.questions[idx] = qErr;
      });
      if (errors.questions.every((qe) => Object.keys(qe).length === 0))
        delete errors.questions;
    }
    return errors;
  };
  const handleLessonSubmit = async (e) => {
    e.preventDefault();
    setLessonLoading(true);
    setLessonError("");
    setLessonSuccess("");
    const errors = validateLessonForm(lessonForm);
    setLessonFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      setLessonLoading(false);
      setLessonError("Please fix the errors in the form.");
      return;
    }
    try {
      await createLesson(lessonForm);
      setLessonSuccess("Lesson created successfully!");
      setLessonForm({
        category: "food",
        unit: 1,
        unit_name: "",
        chapter: 1,
        chapter_name: "",
        questions: [],
        glossary: {},
        difficulty: "beginner",
        content: { section1: "", section2: "" },
        restaurant_uuid: "92cd5a0a-cf6e-4639-a2eb-ed831427dccd",
        menu_items: [],
      });
      setTimeout(() => setShowLessonDrawer(false), 1000);
    } catch (err) {
      setLessonError("Failed to create lesson");
      console.log(lessonFormErrors);
    } finally {
      setLessonLoading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      setUploadError("Please select a file");
      return;
    }

    const allowedMimeTypes = ["application/json"];
    const allowedExtensions = [".json"];
    const fileExtension = `.${selectedFile.name
      .split(".")
      .pop()
      .toLowerCase()}`;

    if (
      !allowedMimeTypes.includes(selectedFile.type) &&
      !allowedExtensions.includes(fileExtension)
    ) {
      setUploadError("Please upload JSON file (.json)");
      return;
    }

    setIsUploading(true);
    setUploadError("");

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      await bulkUploadLessons(formData, "lesson");
      setShowBulkUploadModal(false);
      setSelectedFile(null);
      setSnackbarMessage("Bulk upload successful!");
      setSnackbarOpen(true);
      fetchLessonProgress();
    } catch (error) {
      setUploadError(
        error?.response?.data?.error ||
        "Error uploading file. Please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200 border-t-red-500"></div>
      </div>
    );
  }

  const handleFrequencyChange = async (userId, frequency) => {
    // Current user state backup for rollback
    const previousUsers = [...users];

    // Optimistic Update
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.uuid === userId
          ? { ...user, frequency: `${frequency} days` }
          : user
      )
    );

    try {
      await authService.updateUser(userId, {
        lesson_frequency: frequency,
      });
      setSnackbarMessage(`Frequency updated to ${frequency} days`);
      setSnackbarOpen(true);
      setActiveFrequencyMenu(null);
    } catch (error) {
      console.error("Error updating frequency:", error);
      // Rollback
      setUsers(previousUsers);
      setSnackbarMessage("Failed to update frequency.");
      setSnackbarOpen(true);
      setActiveFrequencyMenu(null);
    }
  };

  return (
    <div className="flex min-h-screen ">
      <div className="flex-1 overflow-auto">
        <main className="p-6 space-y-6">
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Active Trainers Card */}
            <div className="bg-white rounded-[1.5rem] border border-[#F3E8E0] p-6 shadow-sm text-left">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-base font-black text-[#111827] tracking-tight">
                    Active Trainers
                  </h2>
                  <p className="text-[13px] text-gray-400 font-bold mt-1">
                    Total Employees In Training
                  </p>
                </div>
                <div className="bg-[#FCF7F2] px-3 py-1 rounded-full text-[12px] font-black text-[#881337]">
                  {progressData.inActiveEmployees} Inactive
                </div>
              </div>
              <div className="mt-2">
                <span className="text-4xl font-black text-[#111827]">
                  {progressData.activeEmployees}
                </span>
              </div>
            </div>

            {/* Overdue Card */}
            <div className="bg-white rounded-[1.5rem] border border-[#F3E8E0] p-6 shadow-sm text-left">
              <div className="mb-4">
                <h2 className="text-base font-black text-[#111827] tracking-tight">
                  Overdue
                </h2>
                <p className="text-[13px] text-gray-400 font-bold mt-1">
                  out of {progressData.activeEmployees} active trainees
                </p>
              </div>
              <div className="mt-2">
                <span className="text-4xl font-black text-[#C1121F]">
                  {progressData.overDueEmployees}
                </span>
              </div>
            </div>
          </div>

          {/* Lesson Completion Overview */}
          <div className="bg-white rounded-[1.5rem] border border-[#F3E8E0] p-6 shadow-sm">
            <h2 className="text-lg font-black text-[#111827] mb-8 text-left">
              Lesson Completion Overview
            </h2>

            <div className="space-y-8 pb-4">
              {/* Food Knowledge */}
              <div>
                <div className="flex justify-between items-end mb-3">
                  <h3 className="text-sm font-black text-[#111827]">Food</h3>
                  <span className="text-sm font-black text-[#111827]">
                    {Math.round(progressData.foodKnowledge)}%
                  </span>
                </div>
                <div className="h-2.5 w-full bg-[#F3E8E0] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#881337] rounded-full transition-all duration-500"
                    style={{ width: `${progressData.foodKnowledge}%` }}
                  />
                </div>
              </div>

              {/* Wine Knowledge */}
              <div>
                <div className="flex justify-between items-end mb-3">
                  <h3 className="text-sm font-black text-[#111827]">
                    Wine Knowledge
                  </h3>
                  <span className="text-sm font-black text-[#111827]">
                    {Math.round(progressData.wineKnowledge)}%
                  </span>
                </div>
                <div className="h-2.5 w-full bg-[#F3E8E0] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#D1A181] rounded-full transition-all duration-500"
                    style={{ width: `${progressData.wineKnowledge}%` }}
                  />
                </div>
              </div>

              {/* Beer Knowledge */}
              <div>
                <div className="flex justify-between items-end mb-3">
                  <h3 className="text-sm font-black text-[#111827]">
                    Beer Knowledge
                  </h3>
                  <span className="text-sm font-black text-[#111827]">
                    {Math.round(progressData.beerKnowledge || 0)}%
                  </span>
                </div>
                <div className="h-2.5 w-full bg-[#F3E8E0] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#F59E0B] rounded-full transition-all duration-500"
                    style={{ width: `${progressData.beerKnowledge || 0}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Recent Training Activity */}
          <div className="bg-white rounded-[1.5rem] border border-[#F3E8E0] p-6 shadow-sm">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-lg font-black text-[#111827]">
                Recent Training Activity
              </h2>
              <div className="flex mb-6 items-center gap-2 text-xs text-gray-600">
                <span className="font-medium">Show:</span>
                <Dropdown
                  label={`${activityItemsPerPage}`}
                  options={[
                    { value: "10", label: "10" },
                    { value: "25", label: "25" },
                    { value: "50", label: "50" },
                  ]}
                  selectedOption={activityItemsPerPage.toString()}
                  onSelect={(option) => {
                    setActivityItemsPerPage(Number(option.value));
                    setActivitiesPage(1);
                  }}
                  buttonClassName="h-[34px] px-2 py-1 text-xs font-medium border-gray-200 inline-flex items-center"
                />
              </div>
            </div>

            <div
              className={`space-y-6 mb-8 ${progressData.recentActivities.length > 0 ? "min-h-[300px]" : ""
                }`}
            >
              {progressData.recentActivities.length > 0 ? (
                progressData.recentActivities
                  .slice(
                    (activitiesPage - 1) * activityItemsPerPage,
                    activitiesPage * activityItemsPerPage
                  )
                  .map((activity, idx) => {
                    // Older entries record completion as chapterPassed.
                    const isCompleted = activity.details?.isCompleted ?? activity.details?.chapterPassed;
                    const user = activity.user_uuid;
                    const lesson = activity.details?.lesson_uuid;
                    const name = user
                      ? `${user.first_name} ${user.last_name}`
                      : "Unknown User";
                    // No made-up lesson names: if the lesson can't be resolved, say so plainly.
                    const category = lesson?.category || null;
                    const actionText = isCompleted ? "completed" : "started";
                    const chapterText = lesson
                      ? `${lesson.category === "food" ? "Food" : lesson.category === "beer" ? "Beer" : "Wine"} Unit ${lesson.unit
                      }, Chapter ${lesson.chapter}${lesson.chapter_name ? `: ${lesson.chapter_name}` : ""}`
                      : "a lesson";

                    const timeAgo = activity.timestamp
                      ? formatDate(activity.timestamp)
                      : "";

                    return (
                      <div
                        key={activity._id || idx}
                        className="flex items-start text-left group"
                      >
                        <div className="flex-shrink-0 mt-1.5 mr-4">
                          <div
                            className={`w-2 h-2 rounded-full ${category === "wine"
                              ? "bg-[#D1A181]"
                              : category === "beer"
                                ? "bg-[#F59E0B]"
                                : category === "food"
                                  ? "bg-[#881337]"
                                  : "bg-gray-300"
                              }`}
                          />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-600">
                            <span className="text-[#111827] font-black">
                              {name}
                            </span>{" "}
                            {actionText} {chapterText}
                          </p>
                          <p className="text-[12px] text-gray-400 font-bold mt-1">
                            {timeAgo}
                          </p>
                        </div>
                      </div>
                    );
                  })
              ) : (
                <p className="text-gray-500 text-center py-10">
                  No recent activities
                </p>
              )}
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-between items-center pt-6 border-t border-[#F3E8E0]">
              <p className="text-xs font-bold text-gray-400">
                Showing{" "}
                {Math.min(
                  (activitiesPage - 1) * activityItemsPerPage + 1,
                  progressData.recentActivities.length
                )}{" "}
                -{" "}
                {Math.min(
                  activitiesPage * activityItemsPerPage,
                  progressData.recentActivities.length
                )}{" "}
                of {progressData.recentActivities.length} activities
              </p>

              <div className="flex items-center gap-1.5">
                <button
                  className="p-1.5 rounded-lg border border-[#F3E8E0] text-gray-400 hover:bg-gray-50 disabled:opacity-30 transition-colors"
                  onClick={() => setActivitiesPage((p) => Math.max(1, p - 1))}
                  disabled={activitiesPage === 1}
                >
                  <ChevronLeft size={16} />
                </button>

                {Array.from(
                  {
                    length: Math.ceil(
                      progressData.recentActivities.length /
                      activityItemsPerPage
                    ),
                  },
                  (_, i) => i + 1
                ).map((page) => (
                  <button
                    key={page}
                    onClick={() => setActivitiesPage(page)}
                    className={`w-8 h-8 rounded-lg text-xs font-black transition-all ${activitiesPage === page
                      ? "bg-[#881337] text-white shadow-md shadow-[#881337]/20"
                      : "text-gray-500 hover:bg-gray-50 border border-transparent"
                      }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  className="p-1.5 rounded-lg border border-[#F3E8E0] text-gray-400 hover:bg-gray-50 disabled:opacity-30 transition-colors"
                  onClick={() =>
                    setActivitiesPage((p) =>
                      Math.min(
                        Math.ceil(
                          progressData.recentActivities.length /
                          activityItemsPerPage
                        ),
                        p + 1
                      )
                    )
                  }
                  disabled={
                    activitiesPage >=
                    Math.ceil(
                      progressData.recentActivities.length /
                      activityItemsPerPage
                    )
                  }
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Employee Training Status */}
          <div className="bg-white rounded-[1.5rem] border border-[#F3E8E0] p-6 shadow-sm">
            <h2 className="text-lg font-bold mb-4 text-left text-[#111827]">
              Employee Training Status
            </h2>

            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-6 gap-4">
              {/* Filters Group (Left Side) */}
              <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
                {/* Search */}
                <div className="relative w-64">
                  <input
                    type="text"
                    placeholder="Search by name or email"
                    className="w-full pl-9 pr-8 py-1.5 bg-white border border-[#F3E8E0] rounded-lg text-xs font-medium focus:ring-1 focus:ring-[#881337]/20 focus:border-[#881337]/40 transition-all placeholder:text-gray-400"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3.5 w-3.5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Restaurant Dropdown */}
                <Dropdown
                  label="All Restaurants"
                  options={[
                    { value: "All Restaurants", label: "All Restaurants" },
                    ...restaurants.map((restaurant) => ({
                      value: restaurant.name,
                      label: restaurant.name,
                    })),
                  ]}
                  selectedOption={selectedRestaurant}
                  onSelect={(option) => setSelectedRestaurant(option.value)}
                  buttonClassName="h-[34px] px-2 py-1 text-xs font-medium border-gray-200 inline-flex items-center"
                  searchable
                  searchPlaceholder="Search restaurant..."
                />

                {/* Frequency Dropdown (styled like Restaurants) */}
                <div>
                  <Dropdown
                    label={
                      selectedFrequency === "All"
                        ? "All Freq"
                        : selectedFrequency
                    }
                    options={[
                      { value: "All", label: "All Freq" },
                      { value: "3 days", label: "3 days" },
                      { value: "5 days", label: "5 days" },
                      { value: "7 days", label: "7 days" },
                    ]}
                    selectedOption={selectedFrequency}
                    onSelect={(option) => setSelectedFrequency(option.value)}
                    buttonClassName="h-[34px] px-2 py-1 text-xs font-medium border-gray-200 inline-flex items-center"
                  />
                </div>

                {/* Status Segmented Control (matches attached image) */}
                <div className="rounded bg-[#FAF5F1] p-1 flex items-center gap-1">
                  {/* <button
                    onClick={() => setSelectedStatus("All")}
                    className={`px-3 py-1.5 rounded- text-xs font-black transition-all ${
                      selectedStatus === "All"
                        ? "bg-white text-[#111827] shadow-sm"
                        : "text-gray-500"
                    }`}
                  >
                    All
                  </button> */}

                  <button
                    onClick={() => setSelectedStatus("On Track")}
                    className={`px-3 py-1.5 rounded text-xs font-black transition-all ${selectedStatus === "On Track"
                      ? "bg-white text-[#111827] shadow-sm"
                      : "text-gray-500"
                      }`}
                  >
                    On
                  </button>

                  <button
                    onClick={() => setSelectedStatus("Overdue")}
                    className={`px-3 py-1.5 rounded text-xs font-black transition-all ${selectedStatus === "Overdue"
                      ? "bg-white text-[#111827] shadow-sm"
                      : "text-gray-500"
                      }`}
                  >
                    Overdue
                  </button>
                </div>

                {/* Clear All Filters */}
                {(searchQuery ||
                  selectedRestaurant !== "All Restaurants" ||
                  selectedStatus !== "All" ||
                  selectedFrequency !== "All") && (
                    <button
                      onClick={handleClearFilters}
                      className="text-xs font-bold text-red-600 hover:text-red-800 transition-colors px-2"
                    >
                      Clear All
                    </button>
                  )}
              </div>
              {/* Action Buttons Group (Right Side) */}
              <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto justify-end">
                {/* Rows per page dropdown - Moved here for better grouping */}
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="font-medium">Show:</span>
                  <Dropdown
                    label={`${tableItemsPerPage}`}
                    options={[
                      { value: "10", label: "10" },
                      { value: "25", label: "25" },
                      { value: "50", label: "50" },
                    ]}
                    selectedOption={tableItemsPerPage.toString()}
                    onSelect={(option) => {
                      setTableItemsPerPage(Number(option.value));
                      setTableCurrentPage(1);
                    }}
                    buttonClassName="h-[34px] px-2 py-1 text-xs font-medium border-gray-200 inline-flex items-center"
                  />
                </div>

                {/* Reset Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowResetOptions(!showResetOptions)}
                    disabled={selectedEmployees.length === 0}
                    className={`flex items-center px-6 py-2 rounded-xl text-[13px] font-bold transition-all shadow-sm ${selectedEmployees.length === 0
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-[#B3888E] text-white hover:bg-[#A67B81]"
                      }`}
                  >
                    <RotateCcw size={16} className="mr-2.5" />
                    Reset Training{" "}
                    {selectedEmployees.length > 0
                      ? `(${selectedEmployees.length})`
                      : ""}
                    <ChevronDown className="ml-2.5 h-4 w-4" />
                  </button>
                  {showResetOptions && (
                    <div className="absolute right-0 mt-2 w-64 bg-white border border-[#F3E8E0] rounded-xl shadow-lg z-20 overflow-hidden">
                      <div className="p-3 border-b border-[#F3E8E0] bg-[#FCF7F2]">
                        <h3 className="text-xs font-black text-[#111827]">
                          Reset Options
                        </h3>
                      </div>
                      <div className="p-2 space-y-1">
                        {["all", "food", "wine", "beer"].map((v) => (
                          <button
                            key={v}
                            onClick={() => setResetVariable(v)}
                            className={`w-full text-left px-3 py-2 text-xs font-bold rounded-lg flex items-center transition-colors ${resetVariable === v
                              ? "bg-[#881337] text-white"
                              : "hover:bg-gray-50 text-gray-600"
                              }`}
                          >
                            Reset {v.charAt(0).toUpperCase() + v.slice(1)}{" "}
                            Training
                          </button>
                        ))}
                      </div>
                      <div className="p-3 border-t border-[#F3E8E0] bg-[#FCF7F2]">
                        <button
                          onClick={() => handleResetTraining()}
                          className="w-full py-2 bg-[#881337] text-white text-xs font-black rounded-lg shadow-sm"
                          disabled={showResetConfirmation || resetVariable === ""}
                        >
                          {showResetConfirmation
                            ? "Resetting..."
                            : "Confirm Reset"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>


            {/* Users Table */}
            <div className="overflow-x-auto min-h-[400px] border border-[#F3E8E0] rounded-t-[1.5rem]">
              <table className="w-full">
                <thead className="bg-[#FAF5F1]">
                  <tr className="border-b border-[#F3E8E0]">
                    <th className="px-4 py-5 text-left w-12">
                      <input
                        type="checkbox"
                        checked={
                          selectedEmployees.length === filteredUsers.length &&
                          filteredUsers.length > 0
                        }
                        onChange={handleSelectAll}
                        className="rounded border-[#D1A181] text-[#881337] focus:ring-[#881337]"
                      />
                    </th>

                    <th
                      className="px-4 py-5 text-left text-xs font-black text-gray-500 uppercase tracking-widest cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center gap-1">
                        Employee Name
                        {sortBy === "name" ? (
                          sortDirection === "asc" ? (
                            <ChevronUp size={12} className="text-[#881337]" />
                          ) : sortDirection === "desc" ? (
                            <ChevronDown size={12} className="text-[#881337]" />
                          ) : (
                            <ChevronsUpDown
                              size={12}
                              className="text-gray-400"
                            />
                          )
                        ) : (
                          <ChevronsUpDown size={12} className="text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-5 text-left text-xs font-black text-gray-500 uppercase tracking-widest cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort("restaurant")}
                    >
                      <div className="flex items-center gap-1">
                        Restaurant
                        {sortBy === "restaurant" ? (
                          sortDirection === "asc" ? (
                            <ChevronUp size={12} className="text-[#881337]" />
                          ) : sortDirection === "desc" ? (
                            <ChevronDown size={12} className="text-[#881337]" />
                          ) : (
                            <ChevronsUpDown
                              size={12}
                              className="text-gray-400"
                            />
                          )
                        ) : (
                          <ChevronsUpDown size={12} className="text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-5 text-left text-xs font-black text-gray-500 uppercase tracking-widest w-40 cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort("food")}
                    >
                      <div className="flex items-center gap-1">
                        Food
                        {sortBy === "food" ? (
                          sortDirection === "asc" ? (
                            <ChevronUp size={12} className="text-[#881337]" />
                          ) : sortDirection === "desc" ? (
                            <ChevronDown size={12} className="text-[#881337]" />
                          ) : (
                            <ChevronsUpDown
                              size={12}
                              className="text-gray-400"
                            />
                          )
                        ) : (
                          <ChevronsUpDown size={12} className="text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-5 text-left text-xs font-black text-gray-500 uppercase tracking-widest w-40 cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort("wine")}
                    >
                      <div className="flex items-center gap-1">
                        Wine
                        {sortBy === "wine" ? (
                          sortDirection === "asc" ? (
                            <ChevronUp size={12} className="text-[#881337]" />
                          ) : sortDirection === "desc" ? (
                            <ChevronDown size={12} className="text-[#881337]" />
                          ) : (
                            <ChevronsUpDown
                              size={12}
                              className="text-gray-400"
                            />
                          )
                        ) : (
                          <ChevronsUpDown size={12} className="text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-5 text-left text-xs font-black text-gray-500 uppercase tracking-widest w-40 cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort("beer")}
                    >
                      <div className="flex items-center gap-1">
                        Beer
                        {sortBy === "beer" ? (
                          sortDirection === "asc" ? (
                            <ChevronUp size={12} className="text-[#881337]" />
                          ) : sortDirection === "desc" ? (
                            <ChevronDown size={12} className="text-[#881337]" />
                          ) : (
                            <ChevronsUpDown
                              size={12}
                              className="text-gray-400"
                            />
                          )
                        ) : (
                          <ChevronsUpDown size={12} className="text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-5 text-left text-xs font-black text-gray-500 uppercase tracking-widest cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort("frequency")}
                    >
                      <div className="flex items-center gap-1">
                        Frequency
                        {sortBy === "frequency" ? (
                          sortDirection === "asc" ? (
                            <ChevronUp size={12} className="text-[#881337]" />
                          ) : sortDirection === "desc" ? (
                            <ChevronDown size={12} className="text-[#881337]" />
                          ) : (
                            <ChevronsUpDown
                              size={12}
                              className="text-gray-400"
                            />
                          )
                        ) : (
                          <ChevronsUpDown size={12} className="text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-5 text-left text-xs font-black text-gray-500 uppercase tracking-widest cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {sortBy === "status" ? (
                          sortDirection === "asc" ? (
                            <ChevronUp size={12} className="text-[#881337]" />
                          ) : sortDirection === "desc" ? (
                            <ChevronDown size={12} className="text-[#881337]" />
                          ) : (
                            <ChevronsUpDown
                              size={12}
                              className="text-gray-400"
                            />
                          )
                        ) : (
                          <ChevronsUpDown size={12} className="text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-5 text-center text-xs font-black text-gray-500 uppercase tracking-widest">
                      Lesson History
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#F3E8E0]">
                  {paginatedUsers.map((user, i) => (
                    <Fragment key={user.uuid || i}>
                      <tr className="hover:bg-gray-50/50 transition-colors group">
                        <td className="w-16 px-6 py-5">
                          <input
                            type="checkbox"
                            className="rounded border-[#F3E8E0] text-[#881337] focus:ring-[#881337]"
                            onChange={() => handleSelectEmployee(user.uuid)}
                            checked={selectedEmployees.includes(user.uuid)}
                          />
                        </td>
                        <td className="px-4 py-5">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gray-100 flex-shrink-0 overflow-hidden ring-2 ring-white shadow-sm">
                              <img
                                src={`https://ui-avatars.com/api/?name=${user.first_name}+${user.last_name}&background=random`}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="text-left leading-tight">
                              <div className="text-sm font-bold text-[#111827]">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-[11px] font-semibold text-gray-400 mt-0.5">
                                {user.email || "-"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-5">
                          <div className="text-sm font-bold text-gray-500/80 text-left">
                            {user.restaurants && Array.isArray(user.restaurants)
                              ? user.restaurants.map(r => r?.name || r).join(", ")
                              : "-"}
                          </div>
                        </td>
                        <td className="px-4 py-5 w-40">
                          <div className="flex items-center gap-3">
                            <div className="h-1.5 flex-grow bg-[#F3E8E0] rounded-full overflow-hidden min-w-[60px]">
                              <div
                                className="h-full bg-[#881337] rounded-full"
                                style={{ width: `${user.foodKnowledge}%` }}
                              />
                            </div>
                            <span className="text-[11px] font-bold text-[#111827] w-8">
                              {Math.round(user.foodKnowledge)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-5 w-40">
                          <div className="flex items-center gap-3">
                            <div className="h-1.5 flex-grow bg-[#F3E8E0] rounded-full overflow-hidden min-w-[60px]">
                              <div
                                className="h-full bg-[#D1A181] rounded-full"
                                style={{ width: `${user.wineKnowledge}%` }}
                              />
                            </div>
                            <span className="text-[11px] font-bold text-[#111827] w-8">
                              {Math.round(user.wineKnowledge)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-5 w-40">
                          <div className="flex items-center gap-3">
                            <div className="h-1.5 flex-grow bg-[#F3E8E0] rounded-full overflow-hidden min-w-[60px]">
                              <div
                                className="h-full bg-[#F59E0B] rounded-full"
                                style={{ width: `${user.beerKnowledge || 0}%` }}
                              />
                            </div>
                            <span className="text-[11px] font-bold text-[#111827] w-8">
                              {Math.round(user.beerKnowledge || 0)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-5">
                          <div className="relative inline-flex items-center gap-2 text-sm font-bold text-gray-600 bg-[#FAF5F1] px-3 py-1.5 rounded-full">
                            {user.frequency
                              ? typeof user.frequency === "number"
                                ? `${user.frequency} days`
                                : user.frequency.includes("day")
                                  ? user.frequency.endsWith("s")
                                    ? user.frequency
                                    : `${user.frequency}s`
                                  : `${user.frequency} days`
                              : "3 days"}
                            {canEditFrequency && (
                              <div className="relative flex items-center">
                                <Settings
                                  size={13}
                                  className={`cursor-pointer transition-colors ${activeFrequencyMenu === user.uuid
                                    ? "text-[#881337]"
                                    : "text-gray-400 hover:text-gray-600"
                                    }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveFrequencyMenu(
                                      activeFrequencyMenu === user.uuid
                                        ? null
                                        : user.uuid
                                    );
                                  }}
                                />

                                {activeFrequencyMenu === user.uuid && (
                                  <div className="absolute left-0 top-full mt-2 w-40 bg-white border border-[#F3E8E0] rounded-xl shadow-lg z-30 overflow-hidden py-1">
                                    <div className="px-3 py-1.5 border-b border-[#F3E8E0] bg-[#FCF7F2]">
                                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                                        Set Frequency
                                      </span>
                                    </div>
                                    {[3, 5, 7].map((num) => {
                                      const isCurrent =
                                        parseInt(user.frequency || 3) === num;
                                      return (
                                        <button
                                          key={num}
                                          onClick={() =>
                                            handleFrequencyChange(
                                              user.uuid,
                                              num
                                            )
                                          }
                                          disabled={isCurrent}
                                          className={`w-full text-left px-4 py-2 text-xs font-bold transition-colors flex items-center justify-between ${isCurrent
                                            ? "bg-[#FAF5F1] text-[#881337] cursor-default"
                                            : "hover:bg-gray-50 text-gray-700"
                                            }`}
                                        >
                                          Every {num} days
                                          {isCurrent && (
                                            <CheckCircle2
                                              size={12}
                                              className="text-[#881337]"
                                            />
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-5">
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black ${user.status === "On Track"
                              ? "bg-[#E6F4EA] text-[#137333]"
                              : "bg-[#FCE7E8] text-[#881337]"
                              }`}
                          >
                            {user.status === "On Track" ? (
                              <CheckCircle2 size={12} strokeWidth={3} />
                            ) : (
                              <AlertCircle size={12} strokeWidth={3} />
                            )}
                            {user.status}
                          </span>
                        </td>
                        <td className="px-4 py-5 text-center">
                          <button
                            onClick={() => toggleHistory(user.uuid)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-all text-gray-400 hover:text-[#881337]"
                          >
                            <ChevronDown
                              size={18}
                              className={`transition-transform duration-300 ${expandedHistory === user.uuid
                                ? "rotate-180"
                                : ""
                                }`}
                            />
                          </button>
                        </td>
                      </tr>

                      {expandedHistory === user.uuid && (
                        <tr key={i}>
                          <td colSpan="9" className="px-4 py-4 bg-gray-50">
                            <div className="space-y-4">
                              <h3 className="text-sm font-medium text-gray-900">
                                Lesson History
                              </h3>
                              {user.lessonHistory &&
                                user.lessonHistory.length > 0 ? (
                                <div className="space-y-3">
                                  {user.lessonHistory.map((lesson, index) => (
                                    <div
                                      key={index}
                                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                                    >
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          {lesson.category === "food"
                                            ? "Food"
                                            : lesson.category === "beer"
                                              ? "Beer"
                                              : "Wine"}{" "}
                                          Unit {lesson.unit}, Chapter{" "}
                                          {lesson.chapter}
                                        </div>
                                        <div
                                          className="text-xs text-gray-500 mt-1"
                                          style={{ textAlign: "left" }}
                                        >
                                          {lesson.progress.status}{" "}
                                          {formatDate(
                                            lesson.progress.completedAt
                                          )}
                                        </div>
                                      </div>
                                      <div>
                                        <span
                                          className={`inline-block px-4 py-1 rounded-full text-xs font-medium ${lesson.progress.completedAt >
                                            lesson.progress.dueDate
                                            ? "bg-[#e1e1e1] text-black"
                                            : "bg-[#e1e1e1] text-black"
                                            }`}
                                        >
                                          {lesson.progress.completedAt >
                                            lesson.progress.dueDate
                                            ? "Overdue"
                                            : "On Track"}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-6 bg-white rounded-lg border border-gray-200">
                                  <p className="text-sm text-gray-500">
                                    No lesson history available for this
                                    employee
                                  </p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            {totalTableItems > 0 && (
              <div className="px-4 sm:px-6 py-5 bg-white flex flex-col items-center justify-between border-t border-[#F3E8E0] gap-4 sm:flex-row rounded-b-[1.5rem]">
                <div className="text-[12px] sm:text-xs font-bold text-gray-500 text-center sm:text-left">
                  Showing {(tableCurrentPage - 1) * tableItemsPerPage + 1} to{" "}
                  {Math.min(
                    tableCurrentPage * tableItemsPerPage,
                    totalTableItems
                  )}{" "}
                  of {totalTableItems} results
                </div>

                <div className="flex items-center gap-2">
                  <button
                    disabled={tableCurrentPage === 1}
                    onClick={() => setTableCurrentPage(tableCurrentPage - 1)}
                    className="flex items-center text-[12px] sm:text-xs font-bold text-gray-400 hover:text-[#881337] disabled:text-gray-200 transition-colors px-2 sm:px-3 py-1"
                  >
                    Prev
                  </button>

                  <div className="flex items-center gap-1.5">
                    {[...Array(totalTablePages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === totalTablePages ||
                        (page >= tableCurrentPage - 1 &&
                          page <= tableCurrentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setTableCurrentPage(page)}
                            className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-black transition-all ${tableCurrentPage === page
                              ? "bg-[#881337] text-white shadow-md shadow-[#881337]/20"
                              : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-100"
                              }`}
                          >
                            {page}
                          </button>
                        );
                      }
                      if (
                        page === tableCurrentPage - 2 ||
                        page === tableCurrentPage + 2
                      ) {
                        return (
                          <span key={page} className="text-gray-400 px-1">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    disabled={tableCurrentPage === totalTablePages}
                    onClick={() => setTableCurrentPage(tableCurrentPage + 1)}
                    className="flex items-center text-[12px] sm:text-xs font-bold text-gray-400 hover:text-[#881337] disabled:text-gray-200 transition-colors px-2 sm:px-3 py-1"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      {showLessonDrawer && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-black opacity-30"
            onClick={() => setShowLessonDrawer(false)}
          ></div>
          <div className="fixed inset-y-0 right-0 w-full md:w-1/2 lg:w-[50%] bg-white border-l border-gray-200 shadow-lg z-50 overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-textcolor">
                Create New Lesson
              </h2>
              <button
                onClick={() => setShowLessonDrawer(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-textcolor"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleLessonSubmit} className="space-y-4">
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      name="category"
                      value={lessonForm.category}
                      onChange={handleLessonFormChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      <option value="food">Food</option>
                      <option value="wine">Wine</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Difficulty
                    </label>
                    <select
                      name="difficulty"
                      value={lessonForm.difficulty}
                      onChange={handleLessonFormChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>
                </div>
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Restaurant
                    </label>
                    <select
                      name="restaurant_uuid"
                      value={lessonForm.restaurant_uuid}
                      onChange={handleLessonFormChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      <option value="">Select Restaurant</option>
                      {restaurants.map((r) => (
                        <option key={r.uuid || r.id} value={r.uuid || r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Menu
                    </label>
                    <select
                      name="menu_items"
                      value={lessonForm.menu_items[0] || ""}
                      onChange={(e) =>
                        setLessonForm((prev) => ({
                          ...prev,
                          menu_items: [e.target.value],
                        }))
                      }
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      <option value="">Select Menu</option>
                      {menus.map((m) => (
                        <option key={m.uuid || m.id} value={m.uuid || m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit
                    </label>
                    <input
                      name="unit"
                      type="number"
                      value={lessonForm.unit}
                      onChange={handleLessonFormChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit Name
                    </label>
                    <input
                      name="unit_name"
                      value={lessonForm.unit_name}
                      onChange={handleLessonFormChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Chapter
                    </label>
                    <input
                      name="chapter"
                      type="number"
                      value={lessonForm.chapter}
                      onChange={handleLessonFormChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Chapter Name
                    </label>
                    <input
                      name="chapter_name"
                      value={lessonForm.chapter_name}
                      onChange={handleLessonFormChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content Section 1
                  </label>
                  <input
                    name="section1"
                    value={lessonForm.content.section1}
                    onChange={handleLessonContentChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content Section 2
                  </label>
                  <input
                    name="section2"
                    value={lessonForm.content.section2}
                    onChange={handleLessonContentChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                    Questions
                  </h3>
                  {lessonForm.questions.map((q, idx) => (
                    <div
                      key={q.uuid}
                      className="border p-2 mb-2 rounded bg-gray-50"
                    >
                      <div className="flex justify-between items-center">
                        <div className="font-semibold">{q.question_text}</div>
                        <button
                          type="button"
                          className="text-red-500"
                          onClick={() => handleRemoveQuestion(idx)}
                        >
                          Remove
                        </button>
                      </div>
                      <div className="text-xs text-gray-500">
                        Type: {q.question_type}, Difficulty: {q.difficulty}
                      </div>
                      <div className="text-xs">
                        Options: {q.options_variable.join(", ")}
                      </div>
                      <div className="text-xs">
                        Correct: {q.correct_answer_variable.join(", ")}
                      </div>
                      {q.hint && (
                        <div className="text-xs text-blue-600 mt-1">
                          Hint: {q.hint}
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="border p-2 rounded mt-2">
                    <input
                      placeholder="Question Text"
                      name="question_text"
                      value={questionDraft.question_text}
                      onChange={handleQuestionDraftChange}
                      className={`w-full p-2 border ${questionDraftError.question_text
                        ? "border-red-500"
                        : "border-gray-300"
                        } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary mb-2`}
                    />
                    {questionDraftError.question_text && (
                      <div className="text-red-500 text-xs mb-1">
                        {questionDraftError.question_text}
                      </div>
                    )}
                    <div className="mb-2">
                      <input
                        placeholder="Hint (optional)"
                        name="hint"
                        value={questionDraft.hint || ""}
                        onChange={(e) =>
                          setQuestionDraft((prev) => ({
                            ...prev,
                            hint: e.target.value,
                          }))
                        }
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      />
                    </div>
                    <div className="flex space-x-2 mb-2">
                      <select
                        name="question_type"
                        value={questionDraft.question_type}
                        onChange={handleQuestionDraftChange}
                        className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      >
                        <option value="single_select">Single Select</option>
                        <option value="multiple_choice">Multiple Choice</option>
                        <option value="text">Text</option>
                        <option value="true_false">True/False</option>
                        <option value="fill_in_the_blank">
                          Fill in the Blank
                        </option>
                        <option value="short_answer">Short Answer</option>
                      </select>
                      <select
                        name="difficulty"
                        value={questionDraft.difficulty}
                        onChange={handleQuestionDraftChange}
                        className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      >
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </div>
                    {/* Show options only for single_select and multiple_choice */}
                    {(questionDraft.question_type === "single_select" ||
                      questionDraft.question_type === "multiple_choice") && (
                        <div className="mb-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Options
                          </label>
                          {questionDraft.options_variable.map((opt, idx) => (
                            <div key={idx} className="relative">
                              <input
                                value={opt}
                                onChange={(e) =>
                                  handleQuestionOptionsChange(idx, e.target.value)
                                }
                                className={`w-full p-2 border ${questionDraftError.options_variable
                                  ? "border-red-500"
                                  : "border-gray-300"
                                  } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary mb-1`}
                                placeholder={`Option ${idx + 1}`}
                              />
                              {/* Show error only for the first field if options error exists */}
                              {idx === 0 &&
                                questionDraftError.options_variable && (
                                  <div className="text-red-500 text-xs mb-1">
                                    {questionDraftError.options_variable}
                                  </div>
                                )}
                            </div>
                          ))}
                          <button
                            type="button"
                            className="text-blue-500 ml-2"
                            onClick={handleAddOption}
                          >
                            + Option
                          </button>
                        </div>
                      )}
                    {/* Show correct answers for all types */}
                    <div className="mb-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Correct Answers
                      </label>
                      {questionDraft.correct_answer_variable.map((ans, idx) => (
                        <div key={idx} className="relative">
                          <input
                            value={ans}
                            onChange={(e) =>
                              handleCorrectAnswerChange(idx, e.target.value)
                            }
                            className={`w-full p-2 border ${questionDraftError.correct_answer_variable
                              ? "border-red-500"
                              : "border-gray-300"
                              } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary mb-1`}
                            placeholder={`Correct ${idx + 1}`}
                          />
                          {/* Show error only for the first field if correct answer error exists */}
                          {idx === 0 &&
                            questionDraftError.correct_answer_variable && (
                              <div className="text-red-500 text-xs mb-1">
                                {questionDraftError.correct_answer_variable}
                              </div>
                            )}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="text-blue-500 ml-2"
                        onClick={handleAddCorrectAnswer}
                      >
                        + Correct
                      </button>
                    </div>
                    <div className="mb-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Repeat For (optional)
                      </label>
                      <div className="flex space-x-2">
                        <input
                          placeholder="Key Variable"
                          value={questionDraft.repeat_for.key_variable}
                          onChange={(e) =>
                            setQuestionDraft((prev) => ({
                              ...prev,
                              repeat_for: {
                                ...prev.repeat_for,
                                key_variable: e.target.value,
                              },
                            }))
                          }
                          className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        />
                        <input
                          placeholder="Source"
                          value={questionDraft.repeat_for.source}
                          onChange={(e) =>
                            setQuestionDraft((prev) => ({
                              ...prev,
                              repeat_for: {
                                ...prev.repeat_for,
                                source: e.target.value,
                              },
                            }))
                          }
                          className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        />
                      </div>
                    </div>
                    {/* Add Question Button and Validation Errors */}
                    <button
                      type="button"
                      className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90"
                      onClick={handleAddQuestion}
                    >
                      Add Question
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Glossary
                  </label>
                  <input
                    name="glossary"
                    defaultValue={JSON.stringify(lessonForm?.glossary)}
                    onChange={(e) =>
                      setLessonForm((prev) => ({
                        ...prev,
                        glossary: e?.target?.value,
                      }))
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                {/* Hide Create Lesson button until at least one question is added */}
                <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                    onClick={() => setShowLessonDrawer(false)}
                  >
                    Cancel
                  </button>
                  {lessonForm.questions.length > 0 && (
                    <button
                      type="submit"
                      className="px-4 py-2 rounded-md bg-primary text-white hover:bg-primary/90"
                      disabled={lessonLoading}
                    >
                      {lessonLoading ? "Creating..." : "Create Lesson"}
                    </button>
                  )}
                </div>
                {lessonError && (
                  <div className="text-red-500 text-sm mt-2">{lessonError}</div>
                )}
                {lessonSuccess && (
                  <div className="text-green-600 text-sm mt-2">
                    {lessonSuccess}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      )}
      {showBulkUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black opacity-30"
            onClick={() => setShowBulkUploadModal(false)}
          ></div>
          <div className="bg-white rounded-xl shadow-xl z-50 overflow-hidden w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-xl font-black text-[#111827] mb-4">
                Bulk Upload Lessons
              </h2>
              <div
                className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${dragActive ? "border-primary bg-primary/5" : "border-gray-300"
                  }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                    >
                      <span>
                        {selectedFile ? selectedFile.name : "Upload a file"}
                      </span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        accept=".json"
                        className="sr-only"
                        onChange={handleFileChange}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">JSON up to 10MB</p>
                </div>
              </div>

              {uploadError && (
                <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm mt-4">
                  {uploadError}
                </div>
              )}

              <div className="flex justify-between items-center space-x-2 pt-6 mt-6 border-t border-gray-100">
                <a
                  href={"./lesson_template.zip"}
                  download
                  className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-lg transition-colors"
                >
                  Template
                </a>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 text-gray-500 hover:text-gray-700 font-bold text-sm"
                    onClick={() => setShowBulkUploadModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-5 py-2 bg-[#881337] text-white rounded-lg font-bold text-sm shadow-sm hover:bg-[#6c0f2c] transition-colors"
                    onClick={handleBulkUpload}
                    disabled={!selectedFile || isUploading}
                  >
                    {isUploading ? "Uploading..." : "Upload"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}
